const Contact = require('@/lib/Contacts/Contact');
const CurrentRedemptionAction = require('./CurrentRedemptionAction');
const { toBool, toInt, toNullableInt } = require('@/functions/transform');

/**
 * @constructor
 */
const Member = function () {
    /**
     * Simple name/values
     */

    /**
     * @type {number|null}
     */
    this.ID = null;
    this.contactID = null;

    /**
     * The contact contains the member's firstName, lastName, photo, email, etc.
     *
     * @type {Contact}
     */
    this.contact = null;

    /**
     * @type {Number}
     */
    this.membershipID = null;

    // Is the member currently being redeemed or unredeemed or in neither state?
    this.currentRedemptionAction = CurrentRedemptionAction.NONE;
    this.visitedToday = false;
    this.visitCount = 0;

    // Used for maintenance
    this.index = 0; // used for counting up existing ones.
    this.label = 'MEMBERS_AND_ADDRESS.PRIMARY_MEMBER';

    /**
     * Determines whether the user has clicked 'Save Member'. If false, the member may have details stored but will
     * not be included in the membership. A manual save is required.
     *
     * @type {boolean}
     */
    this.saved = false;

    this.saving = false;
};

Member.prototype = {
    getHydrationMap() {
        return {
            ID: {
                field: ['ID', 'memberID'],
                transform: toInt,
            },
            contact: {
                field: ['contact', 'contacts'],
                model: Contact,
                /**
                 * @param {Contact} value
                 * @return {?Contact}
                 */
                transform(value) {
                    // Prevent empty contacts being created but check for firstName rather ID so that we don't
                    // end up removing members when we restore an orderInProgress where usually only the first
                    // member will have a contact.ID as this stage.
                    if (!value || !value.firstName) {
                        return null;
                    }

                    return value;
                },
            },
            contactID: toNullableInt,
            membershipID: toInt,
            saved: toBool,
            visitCount: toInt,
            visitedToday: {
                field: [
                    'visitedToday', // If rehydrating
                    'isRedeemedForDay', // Attached to members on a /memberships API response.
                ],
                transform: toBool,
            },
        };
    },

    afterHydration() {
        if (this.contact && this.contact.lastName) {
            this.saved = true;
        }
    },

    /**
     * This is an important check.
     * Every member "slot" is created when a membership is created, even if no person/contact is assigned to that
     * slot.
     * e.g. For a family plan that allows up to 2 adults and 3 children, 5 'members' will always be created even if
     * only 2 adults and 1 child are actually used. The extra 2 members will have memberIDs but no contactIDs.
     *
     * @return {boolean}
     */
    hasContact: function hasContact() {
        return !!this.contact;
    },

    /**
     * @param {Contact|null} contact
     */
    setContact: function setContact(contact) {
        if (contact) {
            this.contact = contact;
            this.contactID = contact.ID;
        } else {
            this.contact = null;
            this.contactID = null;
        }
    },

    getID: function getID() {
        return this.ID;
    },

    hasVisitedToday: function hasVisitedToday() {
        return this.visitedToday;
    },

    getVisits: function getVisits() {
        return this.visitCount;
    },

    setVisits: function setVisits(memberRedemptionData) {
        // The object passed in contains:
        //  the new member visit count
        //  whether the member has visited today
        this.visitedToday = memberRedemptionData.hasOwnProperty('isRedeemedForDay') ? !!memberRedemptionData.isRedeemedForDay : false;
        this.visitCount = memberRedemptionData.hasOwnProperty('visitCount') ? parseInt(memberRedemptionData.visitCount, 10) : 0;
    },

    setIndex: function setIndex(index) {
        this.index = index;
        if (index === 0) {
            this.label = 'MEMBERS_AND_ADDRESS.PRIMARY_MEMBER';
        } else {
            this.label = ['MEMBERS_AND_ADDRESS.MEMBER', 1 + index];
        }
    },

    /**
     * Returns true if the member has been manually saved (hard saved).
     *
     * @return {boolean}
     */
    isSaved: function isSaved() {
        return !!this.saved;
    },

    /**
     * @return {boolean}
     */
    isSaving: function isSaving() {
        return !!this.saving;
    },

    get redemptionActionInProgress() {
        return this.currentRedemptionAction !== CurrentRedemptionAction.NONE;
    },
};

module.exports = Member;
